import { FocusEvent, useEffect, useReducer, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { useScopedTranslate } from '@locale/translate';

import { api } from 'api';
import { useModal } from 'features/modals';

import {
  CHEER_BEFORE_REDIRECT_MS,
  getLoginErrorBody,
  getMoonFace,
  getMoonFaceChangeIn,
  getMoonLook,
  getPuffinMood,
  getSigninErrors,
  getSigninFocus,
  isSsoRequired,
  isValidEmail,
  SIGNIN_EMAIL_ID,
  SigninFieldErrors,
  SigninFocus,
  SigninSceneState,
  validateSignin,
} from './signin-form-state';

/** The first sign-in step passed; `uuid` is set when the user has a second factor to enter. */
export type TwoFactorChallenge = { needs2FA: true; uuid?: string };

const wait = (ms: number) => {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
};

/**
 * The email and password sign-in form, plus what the Halloween scene should be doing about it.
 * With `shouldCheer`, a successful sign-in starts the cheer and holds the redirect until it has
 * had time to be seen.
 */
export const useSigninForm = ({
  shouldCheer,
  onTwoFactorChallenge,
}: {
  shouldCheer: boolean;
  onTwoFactorChallenge: (challenge: TwoFactorChallenge) => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { showModal } = useModal();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focus, setFocus] = useState<SigninFocus>(null);
  const [errors, setErrors] = useState<SigninFieldErrors>({});
  const [errorAt, setErrorAt] = useState(0);
  const [cheerAt, setCheerAt] = useState(0);
  const [, rerender] = useReducer((count: number) => {
    return count + 1;
  }, 0);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const hasError = Boolean(errors.email || errors.password);

  // The moon's surprise and joy end on their own; re-render when they do.
  useEffect(() => {
    const changeIn = getMoonFaceChangeIn({ now: Date.now(), joyAt: cheerAt, errorAt, hasError });
    if (changeIn === null) {
      return;
    }
    const timeout = setTimeout(rerender, changeIn + 1);
    return () => {
      clearTimeout(timeout);
    };
  }, [cheerAt, errorAt, hasError]);

  const flagErrors = (nextErrors: SigninFieldErrors) => {
    setErrors(nextErrors);
    setErrorAt(Date.now());
  };

  const onEmailChange = (value: string) => {
    // The field repeats its trimmed value on blur; only real edits clear errors.
    if (value === email) {
      return;
    }
    setEmail(value);
    setErrors((current) => {
      return { email: current.email && isValidEmail(value) ? undefined : current.email };
    });
  };

  const onPasswordChange = (value: string) => {
    if (value === password) {
      return;
    }
    setPassword(value);
    setErrors((current) => {
      return { ...current, password: undefined };
    });
  };

  const onFocus = (event: FocusEvent<HTMLElement>) => {
    setFocus(getSigninFocus(event.target.id));
  };

  const onBlur = (event: FocusEvent<HTMLElement>) => {
    if (
      event.target.id === SIGNIN_EMAIL_ID &&
      email.trim() &&
      !isValidEmail(email) &&
      !errors.email
    ) {
      flagErrors({ ...errors, email: t('emailInvalid') });
    }
    // Moving between the two fields keeps the puffins engaged instead of flashing to idle.
    const nextTarget = event.relatedTarget as HTMLElement | null;
    if (!getSigninFocus(nextTarget?.id)) {
      setFocus(null);
    }
  };

  const onSubmit = async () => {
    if (isSubmitting) {
      return;
    }
    const invalid = validateSignin({ email, password });
    if (invalid.email || invalid.password) {
      flagErrors({
        email: invalid.email && t(invalid.email),
        password: invalid.password && t(invalid.password),
      });
      return;
    }
    setErrors({});
    setIsSubmitting(true);
    const response = await api.users.signIn(email.trim(), password);
    if (!isMounted.current) {
      return;
    }

    if (response.error) {
      setIsSubmitting(false);
      // The fields were disabled while signing in, which takes focus away from them.
      setFocus(getSigninFocus(document.activeElement?.id));
      const body = getLoginErrorBody(response.data);
      if (isSsoRequired(body)) {
        showModal('SSO_REQUIRED', { email: email.trim() });
        return;
      }
      flagErrors(
        getSigninErrors(body, {
          wrongCredentials: t('wrongCredentials'),
          unknownError: t('unknownError'),
        }),
      );
      return;
    }

    if ('needs2FA' in response.data && response.data.needs2FA) {
      setIsSubmitting(false);
      onTwoFactorChallenge({
        needs2FA: true,
        uuid: 'uuid' in response.data ? response.data.uuid : undefined,
      });
      return;
    }

    if (shouldCheer) {
      setCheerAt(Date.now());
      await wait(CHEER_BEFORE_REDIRECT_MS);
    }
    await queryClient.resetQueries();
    const redirectTo = location.state && (location.state.from ? location.state.from : '/');
    if (redirectTo) {
      navigate(redirectTo);
    }
  };

  const scene: SigninSceneState = {
    mood: getPuffinMood(focus, isPasswordVisible),
    moonFace: getMoonFace({ now: Date.now(), joyAt: cheerAt, errorAt, hasError, focus }),
    moonLook: getMoonLook(focus, isPasswordVisible),
    cheerAt,
  };

  return {
    form: {
      email,
      password,
      errors,
      isPasswordVisible,
      isSubmitting,
      onEmailChange,
      onPasswordChange,
      onPasswordVisibilityChange: setIsPasswordVisible,
      onFocus,
      onBlur,
      onSubmit,
    },
    scene,
  };
};

export type SigninFormState = ReturnType<typeof useSigninForm>['form'];

const locale = {
  en: {
    emailRequired: 'Enter your email address',
    emailInvalid: 'That email address doesn’t look right. Check for typos, like name@company.com',
    passwordRequired: 'Enter your password',
    wrongCredentials: 'That email and password don’t match. Try again or reset your password.',
    unknownError: 'Something went wrong. Please try again.',
  },
  is: {
    emailRequired: 'Sláðu inn netfangið þitt',
    emailInvalid:
      'Þetta netfang lítur ekki rétt út. Athugaðu hvort það leynist innsláttarvilla, t.d. nafn@fyrirtaeki.is',
    passwordRequired: 'Sláðu inn lykilorðið þitt',
    wrongCredentials:
      'Netfangið og lykilorðið passa ekki saman. Reyndu aftur eða endurstilltu lykilorðið.',
    unknownError: 'Eitthvað fór úrskeiðis. Reyndu aftur.',
  },
};
